import _ from "lodash";
import { API_HOST, API_PATH } from "../config/_entrypoint";
import { ApiErrorResponse } from "../types/ApiErrorResponse";
import { loadToken } from "./localStorage";

const apiEndpoint = API_HOST + API_PATH;
const defaultAcceptHeader = "application/ld+json";
const defaultContentTypeHeader = "application/ld+json";
type ApiResponseFormat = "json" | "text";

type SubmissionErrors = Record<string, string> | string[];

export class ResponseError extends Error {
  constructor(
    message: string,
    public readonly status: number
  ) {
    super(message);
    this.name = "ResponseError";
  }
}

export class SubmissionError extends ResponseError {
  constructor(
    message: string,
    status: number,
    public readonly errors: SubmissionErrors
  ) {
    super(message, status);
    this.name = "SubmissionError";
  }
}

class ApiClient {
  async getToken(credentials: { email: string; password: string }) {
    const json = this.post("login_check", { body: JSON.stringify(credentials) });
    const token = _.get(await json, "token");
    if (!token || !_.isString(token)) {
      throw new Error(
        "Für diese Kombination aus E-Mail-Adresse und Passwort konnten keine Daten gefunden werden." +
          " Bitte überprüfen Sie Ihre eingegebenen Daten."
      );
    }
    return token;
  }

  async get(iri: string, options = {}) {
    return this.call("GET", iri, options);
  }

  async getText(iri: string, options = {}): Promise<string> {
    return this.call("GET", iri, options, "text");
  }

  async post(iri: string, options = {}) {
    return this.call("POST", iri, options);
  }

  async put(iri: string, options = {}) {
    return this.call("PUT", iri, options);
  }

  async patch(iri: string, options = {}) {
    return this.call("PATCH", iri, options);
  }

  async delete(iri: string, options = {}) {
    return this.call("DELETE", iri, options);
  }

  async call(method: string, iri: string, options: Record<string, any>, responseFormat: ApiResponseFormat = "json") {
    const url = apiEndpoint + _.trimStart(iri, "/");

    options.method = method;
    this.defaultOptions(options);

    const response = await fetch(url, options);
    if (response.ok && response.status === 204) {
      return null;
    }

    if (response.status === 403) {
      throw new ResponseError("Access Denied", 403);
    }

    const responseData = responseFormat === "text" ? await response.text() : await response.json();
    if (!response.ok) {
      if (typeof responseData === "string") {
        throw new ResponseError(responseData || response.statusText, response.status);
      }

      throw this.handleError(responseData, response);
    }
    return responseData;
  }

  defaultOptions = (options: Record<string, any>) => {
    if ("undefined" === typeof options.headers) {
      options.headers = new Headers();
    }

    if (null === options.headers.get("Accept")) {
      options.headers.set("Accept", defaultAcceptHeader);
    }

    if (
      "undefined" !== options.body &&
      !(options.body instanceof FormData) &&
      null === options.headers.get("Content-Type")
    ) {
      options.headers.set("Content-Type", defaultContentTypeHeader);
    }

    if (options.method.toUpperCase() === "PATCH" && !(options.body instanceof FormData)) {
      options.headers.set("Content-Type", "application/merge-patch+json");
    }

    if (options.headers.get("Content-Type") === "multipart/form-data") {
      options.headers.delete("Content-Type");
    }

    const token = loadToken();
    if (null !== token && null === options.headers.get("Authorization")) {
      options.headers.set("Authorization", "Bearer " + token);
    }

    if (sessionStorage.getItem("impersonate")) {
      options.headers.set("X-Switch-User", sessionStorage.getItem("impersonate"));
    }
  };

  handleError(json: ApiErrorResponse, response: Response) {
    const message =
      json.detail ||
      json.error ||
      json["hydra:description"] ||
      json.errorMessage ||
      json.message ||
      json.res ||
      response.statusText;

    if (json.violations) {
      const errors: Record<string, string> = {};
      _.map(json.violations, (violation) => (errors[violation.propertyPath] = violation.message));
      return new SubmissionError(message, response.status, errors);
    }

    return new ResponseError(message, response.status);
  }
}

export default new ApiClient();
